import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { getActiveClient, useBackendConfig, useTreasuryClient } from "../lib/client/index.js";
import { Page, PageTitle } from "../Components/page.js";
import { Editable, LoadingSpinner, WithFallback } from "../Components/index.js";
import { Spinner, withSpinner } from "../Components/Spinner.js";
import { ButtonOk, ButtonCancel } from "../Components/Buttons.js";
import { BankingInfoEditable } from "../Components/BankingInfo.js";

function calculateSellValue(sellValue, { dq_sell_price, dq_sell_cost, dq_sell_percentage }) {
  return (sellValue * dq_sell_price - dq_sell_cost) * (1 - dq_sell_percentage);
}

function SellRequestForm({ dqAmount, dqPrecision }) {
  let treasuryConfig = useBackendConfig();
  let dqType = treasuryConfig.sell.dq
  let minSellValue = treasuryConfig.sell.dq_sell_min;
  let sellUnits = treasuryConfig.sell.treasury_units;
  let [inputValue, setInputValue] = useState(() => {
    if (dqAmount >= minSellValue) {
      return minSellValue;
    } else {
      return '';
    }
  });

  let [isSpinning, setIsSpinning] = useState(false);

  let [sellValue, setSellValue] = useState(calculateSellValue(inputValue, treasuryConfig.sell));
  const [showConfirmation, setShowConfirmation] = useState(false);
  const navigate = useNavigate();

  let treasury = useTreasuryClient()
  let { mutate: createSellRequest } = useMutation({
    mutationFn: async function createSellRequestFN(data) {
      withSpinner(setIsSpinning, async _ => {
        return await treasury.createSellRequest(data)
      });
    },
    onSuccess: function onSuccessRefresh() {
      navigate("/sales");
    },
    onError: function printError(e) {
      console.warn(e)
    }
  })

  function submitSellRequest(e) {
    e.preventDefault();
    setIsSpinning(true);
    setShowConfirmation(false);
    let form = e.target;
    let formData = new FormData(form);
    let dq = formData.get("dq");
    let sellValue = parseInt(formData.get("sellValueRange"));
    createSellRequest({ dq, sellValue });
  }

  function isValidInputAmount(n) {
    return (!!n && !!minSellValue && !!dqAmount) && (n >= minSellValue && n <= dqAmount)
  }

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    setSellValue(calculateSellValue(e.target.value, treasuryConfig.sell));
  };

  const onOk = (event) => {
    event.preventDefault();
    setShowConfirmation(true);
  };

  const handleCancel = () => {
    setShowConfirmation(false);
  };

  if (dqAmount < minSellValue) return <label>Insufficient Funds: {dqAmount}</label> 

  return <form onSubmit={submitSellRequest}>
  <div className="space-y-12 sm:space-y-16">
    {/* Hidden input */}
    <input type="hidden" name="dq" value={dqType} />
    
    {/* Main section */}
    <div>
      <p className="mt-1 max-w-2xl text-sm/6 text-gray-600 dark:text-gray-400">
        Select the amount to withdraw below and confirm your transaction.
      </p>

      <div className="mt-10 space-y-8 border-b border-gray-900/10 pb-12 sm:space-y-0 sm:divide-y sm:divide-gray-900/10 sm:border-t sm:border-t-gray-900/10 sm:pb-0 dark:border-white/10 dark:sm:divide-white/10 dark:sm:border-t-white/10">
        
        {/* Current Balance */}
        <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
          <label className="block text-sm/6 font-medium text-gray-900 sm:pt-1.5 dark:text-white">
            Current Balance
          </label>
          <div className="mt-2 sm:col-span-2 sm:mt-0">
            <p className="text-base text-gray-900 dark:text-white">${dqAmount} USD-TDN</p>
          </div>
        </div>

        {/* Amount to Exchange */}
        <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
          <label htmlFor="sellValueInput" className="block text-sm/6 font-medium text-gray-900 sm:pt-1.5 dark:text-white">
            Amount to Exchange
          </label>
          <div className="mt-2 sm:col-span-2 sm:mt-0">
            <div className="flex items-center w-fit rounded-md bg-white pl-3 outline-1 -outline-offset-1 outline-gray-300 focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-indigo-600 dark:bg-white/5 dark:outline-white/10 dark:focus-within:outline-indigo-500">

        <span class="text-gray-900 dark:text-white">$</span><Editable 
                id="sellValueInput" 
                isEditing={true} 
                onChange={handleInputChange}
                className="block min-w-0 max-w-12 grow bg-white py-1.5 pr-3 pl-1 text-base text-gray-900 focus:outline-none sm:text-sm/6 dark:bg-transparent dark:text-white"
                type="number"
                min={minSellValue}
                max={dqAmount}
                value={inputValue}
                required 
              />              <span className="shrink-0 text-base text-gray-500 select-none sm:text-sm/6 dark:text-gray-400 pl-2 pr-2">USD-TDN</span>
            </div>
            
            {/* Slider - only show when dqAmount > minSellValue */}
            {(dqAmount > minSellValue) && (
              <div className="mt-4">
                <input
                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-white/10 dark:focus:outline-indigo-500 appearance-none [&::-webkit-slider-runnable-track]:rounded-full [&::-webkit-slider-runnable-track]:h-5 [&::-webkit-slider-runnable-track]:bg-gray-200 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-indigo-600 [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-track]:rounded-full [&::-moz-range-track]:h-5 [&::-moz-range-track]:bg-gray-200 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:bg-indigo-600 [&::-moz-range-thumb]:border-0 [&::-webkit-slider-thumb]:hover:scale-110 
[&::-webkit-slider-thumb]:hover:ring-4 
[&::-webkit-slider-thumb]:hover:ring-indigo-500/50 
[&::-webkit-slider-thumb]:hover:shadow-lg [&::-moz-range-thumb]:hover:scale-110 
[&::-moz-range-thumb]:hover:ring-4 
[&::-moz-range-thumb]:hover:ring-indigo-500/50 
[&::-moz-range-thumb]:hover:shadow-lg"
                  type="range"
                  id="sellValueRange"
                  name="sellValueRange"
                  min={minSellValue}
                  max={dqAmount}
                  value={inputValue}
                  onChange={handleInputChange}
                  required
                />
              </div>
            )}
            
            {dqAmount == minSellValue && (
              <input type="hidden" name="sellValue" value={inputValue} />
            )}
          </div>
        </div>

        {/* Cost to Exchange */}
        <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
          <label className="block text-sm/6 font-medium text-gray-900 sm:pt-1.5 dark:text-white">
            Cost to Exchange
          </label>
          <div className="mt-2 sm:col-span-2 sm:mt-0">
            <p className={`text-base ${isValidInputAmount(inputValue) ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>
              {isValidInputAmount(inputValue) 
                ? `$${(treasuryConfig.sell.dq_sell_cost + treasuryConfig.sell.dq_sell_percentage * inputValue * treasuryConfig.sell.dq_sell_price).toFixed(2)} USD-TDN`
                : "--"}
            </p>
          </div>
        </div>

        {/* Expected Deposit */}
        <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
          <label className="block text-sm/6 font-medium text-gray-900 sm:pt-1.5 dark:text-white">
            Expected Deposit
          </label>
          <div className="mt-2 sm:col-span-2 sm:mt-0">
            <p className={`text-base ${isValidInputAmount(inputValue) ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>
              {isValidInputAmount(inputValue) 
                ? `$${sellValue.toFixed(2)} ${sellUnits}`
                : "--"}
            </p>
          </div>
        </div>

        {/* Remaining Balance */}
        <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
          <label className="block text-sm/6 font-medium text-gray-900 sm:pt-1.5 dark:text-white">
            Remaining Balance
          </label>
          <div className="mt-2 sm:col-span-2 sm:mt-0">
            <p className={`text-base ${isValidInputAmount(inputValue) ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>
              {isValidInputAmount(inputValue) 
                ? `$${Number(dqAmount - inputValue).toFixed(dqPrecision)} USD-TDN`
                : "--"}
            </p>
          </div>
        </div>

        {/* Processing Spinner */}
        <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
          <div></div>
          <div className="mt-2 sm:col-span-2 sm:mt-0">
            <div className="flex items-center gap-x-3">
              <Spinner hidden={!isSpinning} className="!size-6 text-indigo-600" />
              <div hidden={!isSpinning} className="text-indigo-600">Creating transaction...</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Confirmation Section */}
    {showConfirmation && (
      <div>
        <div className="border-b border-gray-900/10 pb-4 dark:border-white/10">
          <div className="sm:gap-4 sm:py-6">
            <div className="">
              <div className="rounded-lg bg-indigo-50 p-6 dark:bg-indigo-900/20">
                <h3 className="text-base/7 font-semibold text-indigo-900 dark:text-indigo-100">
                  Confirm Exchange
                </h3>
                <p className="mt-2 text-sm/6 text-indigo-700 dark:text-indigo-300">
                  Are you sure you wish to proceed with this exchange?
                </p>
              </div>
      <div className="w-full mt-6 flex items-center gap-x-6">
    <div className="mt-6 text-s text-gray-600 dark:text-gray-400">
        Transactions may take up to 5 business days for submission to the automated clearinghouse networks, and may take longer to settle in your account, depending on your recipient bank.
    </div>

            <ButtonCancel type="button" onClick={handleCancel}>
          Cancel
        </ButtonCancel>
        <ButtonOk type="submit">
          Confirm
        </ButtonOk>
      </div>
        </div>
          </div>
        </div>
      </div>
    )}
  </div>

  {/* Action Buttons */}
  <div className="mt-6 flex items-center justify-end gap-x-6">
    {!isSpinning && !showConfirmation && (
      <>
        <ButtonOk
        type="submit"
        onClick={onOk}
          className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:bg-indigo-500 dark:shadow-none dark:hover:bg-indigo-400 dark:focus-visible:outline-indigo-500"
          disabled={!inputValue || !isValidInputAmount(inputValue) || (dqAmount < minSellValue)}
        >
          Exchange for USD
        </ButtonOk>
      </>
    )}
    
  </div>

    </form>;
    /*
  return <form onSubmit={submitSellRequest} >
    <div className="grid gap-6 grid-rows-1" >
      <input type="hidden" name="dq" value={dqType} />
      <label>Current Balance: {dqAmount} TDN</label>
      <div className="flex flex-row">

        <span className="text-nowrap self-center w-fit">Amount to Exchange:</span>
        <Editable id="sellValueInput" isEditing={true} onChange={handleInputChange}
          className="mx-2 !w-fit text-right "
          type="number"
          min={minSellValue}
          max={dqAmount}
          value={inputValue}
          required />
        <span className="self-center"> TDN</span>
      </div>
      {dqAmount == minSellValue && <input type="hidden" name="sellValue" value={inputValue} />}
      {(dqAmount > minSellValue) &&
        <input
          className="appearance-none bg-transparent
          ~~apple~~

          [&::-webkit-slider-runnable-track]:rounded-full
          [&::-webkit-slider-runnable-track]:h-6
          [&::-webkit-slider-runnable-track]:bg-gray-400
          [&::-webkit-slider-thumb]:appearance-none
          [&::-webkit-slider-thumb]:-translate-y-1
          [&::-webkit-slider-thumb]:rounded-full
          [&::-webkit-slider-thumb]:size-8
          [&::-webkit-slider-thumb]:bg-toda-logo-red-1
          [&::-webkit-slider-thumb]:border-none

          ~~mozilla~~
          [&::-moz-range-track]:rounded-full
          [&::-moz-range-track]:h-6
          [&::-moz-range-track]:bg-gray-400
          [&::-moz-range-thumb]:appearance-none
          [&::-moz-range-thumb]:rounded-full
          [&::-moz-range-thumb]:size-8
          [&::-moz-range-thumb]:bg-toda-logo-red-1
          [&::-moz-range-thumb]:border-none"
          type="range"
          id="sellValueRange"
          name="sellValueRange"
          min={minSellValue}
          max={dqAmount}
          value={inputValue}
          onChange={handleInputChange}
          required
        />}
      <label>Cost to Exchange: {isValidInputAmount(inputValue) ? `${(treasuryConfig.sell.dq_sell_cost + treasuryConfig.sell.dq_sell_percentage * inputValue * treasuryConfig.sell.dq_sell_price).toFixed(2)} ${sellUnits}` : "--"}</label>
      <label>Expected Deposit: {isValidInputAmount(inputValue) ? `${sellValue.toFixed(2)} ${sellUnits}` : "--"}</label>
      <label>Remaining Balance: {isValidInputAmount(inputValue) ? `${Number(dqAmount - inputValue).toFixed(dqPrecision)} TDN` : "--"}</label>
      <div className="flex">
        <Spinner hidden={!isSpinning} className="!size-6 text-info" />
        <div hidden={!isSpinning} className="text-info mx-2">Creating...</div>
      </div>
      {!isSpinning && !showConfirmation &&
        <ButtonOk
          type="button"
          onClick={onOk}
          disabled={!inputValue || !isValidInputAmount(inputValue) || (dqAmount < minSellValue)}>Exchange for USD</ButtonOk>}
      {showConfirmation && (
        <div className="confirmation-dialog">
          <p>Are you sure you want to proceed with the exchange?</p>
          <ButtonCancel type="button" onClick={handleCancel}>No</ButtonCancel>
          <span>&nbsp;</span>
          <ButtonOk type="submit">Yes</ButtonOk>

        </div>
      )}
      <span>
        <sup>Payout transactions may take up to 5 business days for submission to the automated clearinghouse networks, and may take longer to settle in your account, depending on your recipient bank.</sup>
      </span>
    </div>
  </form> */
}



export default function Redeem() {

  let twinClient = getActiveClient();
  let treasuryConfig = useBackendConfig();
  let { data: dq } = useQuery({
    queryKey: ["GET", `/dq/${treasuryConfig?.sell?.dq}`],
    queryFn: function getBalanceFn() {
      return twinClient.balance(treasuryConfig.sell.dq);
    },
    enabled: !!treasuryConfig
  });

  function WithBankingProfileNag({ children }) {
    let treasuryClient = useTreasuryClient();
    let { data: bankingProfile, isPending } = useQuery({
      queryKey: ["GET", `/profile/${treasuryClient.host}`],
      queryFn: function getTreasuryProfileFn() {
        return treasuryClient.getProfile()
      }
    })

    if (bankingProfile) {
      return <>{children}</>
    }
    let [isSkipped, setIsSkipped] = useState(false);
    let [isConfiguring, setIsConfiguring] = useState(false);
    return <WithFallback pred={isPending}
      fallback={<LoadingSpinner msg="Fetching banking profile" />}>
      {!isSkipped && !isConfiguring && <div className="flex flex-col gap-2">
        <div>Banking profile not configured!</div>
        <div className="flex flex-row justify-between">
          <ButtonCancel onClick={_ => setIsSkipped(true)}>Skip</ButtonCancel>
          <ButtonOk onClick={_ => setIsConfiguring(true)}>Configure</ButtonOk>
        </div>
      </div>}
      {!isSkipped && isConfiguring && <BankingInfoEditable isEditingInitial={true} />}
      {isSkipped && !isConfiguring && <>{children}</>}
    </WithFallback>
  }

  return <Page title="Transfer Funds">
    <WithBankingProfileNag>
      <SellRequestForm dqAmount={dq?.balance} dqPrecision={dq?.precision} />
    </WithBankingProfileNag>
  </Page>

}


